/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { Dropdown } from 'semantic-ui-react';

import { presetOptionLabel, presetOptionTitle, sortPresetGroups } from '../../../helpers/schedule-display';

/**
 * The named schedule presets, as one multi-select dropdown.
 *
 * Multi-select rather than single-select because the tag grammar accepts a comma-separated list, so
 * more than one preset really can apply at once, and a single-select would quietly remove that. The
 * `multiple selection` Dropdown is the pattern this package already uses for a list-valued choice, as
 * in `advanced/FilterForm.js`, so the markup is not invented here.
 *
 * Every option rendered here comes from the SchedulingInfo response. There is deliberately no built-in
 * list to fall back on (Requirement 5.2): a hardcoded list would be wrong for any account whose groups
 * table differs, and wrong in a way the user could not detect. When the table could not be read, or
 * defines nothing, the control is disabled and the user is pointed at the other option
 * (Requirements 4.9 and 14.6).
 *
 * Options are labelled, titled and ordered by `presetOptionLabel`, `presetOptionTitle` and
 * `sortPresetGroups` in `schedule-display.js` rather than here, because deciding how a preset reads
 * and where it sits in the list is presentation logic and that is where presentation logic lives. All
 * three derive everything from the rows themselves, so no name is special-cased anywhere in this path.
 *
 * The visible label is the name plus the derived window; the raw name and the administrator's
 * description stay on the option's `title`, discoverable on hover for a support conversation without
 * making the list read unevenly.
 *
 * Presentational only: the selection arrives as a prop and every change is reported through
 * `onChange`. The Schedule_Modal owns the observable state, so this component holds none and does no
 * data fetching.
 *
 * Props:
 * - groups: [{ name, description, days, startMinutes, stopMinutes }]
 * - selectedGroups: string[]
 * - onChange: (nextSelectedGroups) => void
 * - available: boolean, false when the groups table could not be read
 * - disabled: boolean
 */

const PLACEHOLDER = 'Select one or more presets';

const UNAVAILABLE_TEXT = 'Presets could not be read for this account. Use Choose days and times instead.';
const EMPTY_TEXT = 'No presets are defined for this account. Use Choose days and times instead.';

class WorkspaceScheduleGroupList extends React.Component {
  // Only an explicit false means unavailable, so that an omitted prop does not present a working
  // account as broken.
  get available() {
    return this.props.available !== false;
  }

  get groups() {
    // A row without a name cannot be selected, so it is skipped rather than offered blank. This
    // matches the backend's tolerance of a malformed row in the groups table. When the table could
    // not be read there is nothing to offer at all, rather than a stale list behind a notice.
    if (!this.available) return [];

    const named = (this.props.groups || []).filter(
      group => group && typeof group.name === 'string' && group.name.trim().length > 0,
    );

    // Sorted rather than shown in scan order. `sortPresetGroups` returns a new array, so the props
    // this component was handed are left as they were.
    return sortPresetGroups(named);
  }

  get options() {
    return this.groups.map(group => {
      const name = group.name.trim();

      return {
        key: name,
        value: name,
        text: presetOptionLabel(group) || name,
        title: presetOptionTitle(group) || name,
      };
    });
  }

  /**
   * The selected values, as the option values the dropdown can match.
   *
   * A name read back from the tag may differ in case from the row in the groups table, so the
   * comparison is case-insensitive and the option's own casing is what is held. A name the account no
   * longer defines is not turned into an option of its own: the selection marks options, it does not
   * create them, and the window summary is where such a name is reported.
   */
  get value() {
    const byLower = this.options.reduce((acc, option) => {
      acc[option.value.toLowerCase()] = option.value;
      return acc;
    }, {});

    return (this.props.selectedGroups || [])
      .map(entry => (typeof entry === 'string' ? byLower[entry.trim().toLowerCase()] : undefined))
      .filter(entry => entry !== undefined);
  }

  pickerRef = React.createRef();

  /**
   * Brings the opened menu into view. The menu is positioned below the control inside the modal's
   * scrolling body, so when the control sat near the foot of the body the menu opened behind the
   * footer and looked cut off. Scrolling the body to it, once the menu has rendered, fixes that
   * wherever the control ends up.
   */
  handleOpen = () => {
    const defer =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : callback => setTimeout(callback, 0);

    defer(() => {
      const picker = this.pickerRef.current;
      const menu = picker && typeof picker.querySelector === 'function' ? picker.querySelector('.menu') : null;
      if (menu && typeof menu.scrollIntoView === 'function') {
        menu.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  };

  handleChange = (event, data) => {
    if (this.props.disabled) return;

    const onChange = this.props.onChange;
    if (!onChange) return;

    // Semantic reports the whole next selection, which is what the modal holds, so nothing is merged
    // here. The values are the options' own names, so the constructed tag value matches a name the
    // backend will recognise.
    onChange((data.value || []).filter(name => typeof name === 'string'));
  };

  renderGuidance() {
    const unavailable = !this.available;

    return (
      <div
        className="wsch-modal-preset-guidance"
        data-testid={unavailable ? 'schedule-groups-unavailable' : 'schedule-groups-empty'}
        aria-live="polite"
      >
        {unavailable ? UNAVAILABLE_TEXT : EMPTY_TEXT}
      </div>
    );
  }

  render() {
    const options = this.options;
    // Disabled with nothing to choose, in both unavailable states, so the control does not invite the
    // click that cannot do anything.
    const disabled = !!this.props.disabled || options.length === 0;

    return (
      <div className="wsch-modal-preset-picker" data-testid="schedule-groups" ref={this.pickerRef}>
        <Dropdown
          multiple
          selection
          className="wsch-modal-dropdown"
          options={options}
          value={this.value}
          onChange={this.handleChange}
          onOpen={this.handleOpen}
          disabled={disabled}
          placeholder={PLACEHOLDER}
          aria-label="Presets"
          data-testid="schedule-groups-dropdown"
        />
        {options.length === 0 ? this.renderGuidance() : null}
      </div>
    );
  }
}

export default WorkspaceScheduleGroupList;
export { PLACEHOLDER, UNAVAILABLE_TEXT, EMPTY_TEXT };
